import { useRef } from 'react';
import { usePeopleGridColumns } from '../matching-engine.utils';
import CXDataGrid from 'common/components/cx-data-grid/cx-data-grid.component';
import { Box } from '@mui/material';
import {
  PAGE_SIZE,
  ROW_HEIGHT,
  SLOTS,
} from 'features/profile-data-grid/profile-data-grid.constants';
import { useMatchingEngineResults } from './MatchingEngineSearchView';
import { useDataGridVirtualization } from 'libs/mui/data-grid/hooks/useDataGridVirtualization';
import { useMatchingEngineGridState } from '../hooks/useMatchingEngineDataGridState';

export const PeopleGrid: React.FC<{
  people: any[];
}> = ({ people }) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  // We have another call to `useMatchingEngineResults` inside `MatchingEngineSearchView`,
  // and since _this_ component is nested inside the _other_, then calling this hook will actually
  // hit the cache of the parent one, without triggering a double call.
  const {
    isFetching,
    hasNextPage = false,
    fetchNextPage,
    total,
  } = useMatchingEngineResults();

  const { initialState, handleSortModelChange } = useMatchingEngineGridState();

  const { rowCount, fetchMoreRows } = useDataGridVirtualization({
    currentRowCount: people.length,
    totalRowCount: total,
    pageSize: PAGE_SIZE,
    isFetching,
    hasNextPage,
    fetchNextPage,
  });

  const columns = usePeopleGridColumns();
  const records = people.map((person) => {
    return {
      id: person.id,
      profileID: person.id,
      name: person.fullName,
      feedbacks: person.feedbacks,
      manager: person.managers?.manager,
      l2Manager: person.managers?.l2Manager,
      l3Manager: person.managers?.l3Manager,
      allocationMetadata: person.allocationMetadata ?? '',
      numberOfContributions: person.contributionCount,
      lastFeedbackDate: person.lastFeedbackDate,
    };
  });

  return (
    <Box height={700} maxHeight={800} overflow="hidden">
      <div ref={anchorRef}></div>
      <CXDataGrid
        paginationMode="server"
        sortingMode="server"
        rowsLoadingMode="server"
        onSortModelChange={handleSortModelChange}
        initialState={{
          ...initialState, // from useMatchingEngineGridState
          columns: {
            columnVisibilityModel: {
              l2manager: false,
              l3manager: false,
            },
          },
          pinnedColumns: {
            left: ['name'],
          },
        }}
        columns={columns}
        rows={records}
        rowHeight={ROW_HEIGHT}
        rowCount={rowCount}
        onFetchRows={fetchMoreRows}
        loading={isFetching}
        slots={SLOTS}
        slotProps={{
          panel: {
            anchorEl: anchorRef.current,
            placement: 'bottom-end' as const,
            popperOptions: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, ROW_HEIGHT + 2], // this offsets the dropDown of gridActions, change if necessary
                  },
                },
              ],
            },
          },
          footer: {
            displayedRowCount: total,
          },
        }}
      />
    </Box>
  );
};
